import { X } from 'lucide-react'
import PropTypes from 'prop-types'

import SearchWithSelect from '@components/search-with-select/SearchWithSelect'
import { MobileSortControl } from '@components/table-sort'

/** Mobile filter bottom-sheet for the Quotes / Work Orders queues.
 *  Mirrors the desktop filter row: two SearchWithSelect controls (column dropdown
 *  + search box). The Quote/WO page owns the state via useSearchFilters; the
 *  drawer is purely presentational and just closes on Apply. */
export const MobileFilterDrawer = ({
  open,
  onClose,
  primarySearchBy,
  setPrimarySearchBy,
  primaryQuery,
  setPrimaryQuery,
  secondarySearchBy,
  setSecondarySearchBy,
  secondaryQuery,
  setSecondaryQuery,
  searchOptions,
  onClear,
  sortOptions,
  sort,
  onSortChange
}) => {
  if (!open) return null

  const handleClear = () => {
    setPrimaryQuery('')
    setSecondaryQuery('')
    onSortChange?.(null)
    onClear?.()
  }

  return (
    <div className='fixed inset-0 z-40 flex flex-col justify-end' role='dialog' aria-modal='true'>
      <div className='absolute inset-0 bg-black/50' onClick={onClose} />
      <div className='relative z-10 flex max-h-[88vh] flex-col rounded-t-2xl bg-white'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-[#EBEDF0] px-5 py-4'>
          <div className='text-[18px] font-bold text-[#0A1628]'>Filters</div>
          <div className='flex items-center gap-3'>
            <button onClick={handleClear} className='text-[14px] font-semibold text-[#1F6FEB] hover:underline'>
              Clear
            </button>
            <button
              onClick={onClose}
              aria-label='Close filters'
              className='inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#F1F4F8] text-[#475569]'
            >
              <X className='h-4 w-4' />
            </button>
          </div>
        </div>

        {/* Body — two SearchWithSelect rows (column + search) */}
        <div className='flex-1 overflow-y-auto px-5 py-4 space-y-4'>
          {sortOptions && onSortChange && <MobileSortControl options={sortOptions} sort={sort} onChange={onSortChange} />}
          <div className='rounded-lg border border-[#D1D5D8] bg-white px-2'>
            <SearchWithSelect
              selectValue={primarySearchBy}
              onSelectChange={(e) => setPrimarySearchBy(e.target.value)}
              searchValue={primaryQuery}
              onSearchChange={(e) => setPrimaryQuery(e.target.value)}
              options={searchOptions}
              placeholder='Search...'
            />
          </div>
          <div className='rounded-lg border border-[#D1D5D8] bg-white px-2'>
            <SearchWithSelect
              selectValue={secondarySearchBy}
              onSelectChange={(e) => setSecondarySearchBy(e.target.value)}
              searchValue={secondaryQuery}
              onSearchChange={(e) => setSecondaryQuery(e.target.value)}
              options={searchOptions}
              placeholder='Search...'
            />
          </div>
        </div>

        {/* Footer CTA */}
        <div className='border-t border-[#EBEDF0] px-5 py-4'>
          <button
            onClick={onClose}
            className='w-full rounded-xl bg-sidebar py-3.5 text-[15px] font-semibold text-white hover:bg-sidebar/90'
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}

MobileFilterDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  primarySearchBy: PropTypes.string,
  setPrimarySearchBy: PropTypes.func,
  primaryQuery: PropTypes.string,
  setPrimaryQuery: PropTypes.func,
  secondarySearchBy: PropTypes.string,
  setSecondarySearchBy: PropTypes.func,
  secondaryQuery: PropTypes.string,
  setSecondaryQuery: PropTypes.func,
  searchOptions: PropTypes.array,
  onClear: PropTypes.func,
  /** Optional "Sort by" (same columns as the desktop headers); shown when given with onSortChange. */
  sortOptions: PropTypes.array,
  sort: PropTypes.shape({ key: PropTypes.string, dir: PropTypes.oneOf(['asc', 'desc']) }),
  onSortChange: PropTypes.func
}
